import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { DiscussionEntityRef, DiscussionRealtimeEvent, DiscussionMessage } from '../domain/types';
import { DiscussionRealtimeTransport } from '../application/ports';

interface SignalRTransportOptions {
  hubUrl: string;
  getAccessToken: () => Promise<string>;
}

const toEvent = (payload: any): DiscussionRealtimeEvent | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const message: DiscussionMessage = {
    id: String(payload.id || ''),
    entityType: String(payload.entityType || ''),
    entityId: String(payload.entityId || ''),
    senderId: String(payload.senderUserId || payload.senderId || ''),
    senderDisplayName: String(payload.senderDisplayName || 'User'),
    text: String(payload.message || payload.text || ''),
    createdAt: String(payload.createdAt || new Date().toISOString()),
    updatedAt: payload.updatedAt ? String(payload.updatedAt) : undefined,
    clientMessageId: payload.clientMessageId ? String(payload.clientMessageId) : undefined,
  };

  return { type: 'messageCreated', message };
};

export const createSignalRDiscussionTransport = ({ hubUrl, getAccessToken }: SignalRTransportOptions): DiscussionRealtimeTransport => {
  let connection: HubConnection | null = null;
  let disabled = false;
  const handlers = new Set<(event: DiscussionRealtimeEvent) => void>();

  const isConnected = (value: HubConnection | null): value is HubConnection =>
    !!value && value.state === HubConnectionState.Connected;

  const ensureConnection = async () => {
    if (disabled) {
      throw new Error('Live discussion is temporarily unavailable.');
    }

    if (connection && connection.state !== HubConnectionState.Disconnected) {
      return connection;
    }

    connection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: async () => getAccessToken(),
        withCredentials: false,
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    connection.on('MessageCreated', (payload: any) => {
      const event = toEvent(payload);
      if (!event) {
        return;
      }

      handlers.forEach((handler) => handler(event));
    });

    try {
      await connection.start();
    } catch (error) {
      disabled = true;
      connection = null;
      throw error;
    }

    return connection;
  };

  return {
    connect: async () => {
      await ensureConnection();
    },
    disconnect: async () => {
      if (!connection) {
        return;
      }

      if (connection.state !== HubConnectionState.Disconnected) {
        await connection.stop();
      }
    },
    join: async (ref: DiscussionEntityRef) => {
      if (!ref.organizationId) {
        return;
      }
      try {
        const active = await ensureConnection();
        if (!isConnected(active)) {
          return;
        }

        await active.invoke('JoinEntity', String(ref.organizationId), String(ref.entityType), String(ref.entityId));
      } catch {
        // Live updates are optional; REST message flow should keep working.
      }
    },
    leave: async (ref: DiscussionEntityRef) => {
      if (!ref.organizationId) {
        return;
      }

      if (!isConnected(connection)) {
        return;
      }

      try {
        await connection.invoke('LeaveEntity', String(ref.organizationId), String(ref.entityType), String(ref.entityId));
      } catch {
        // Ignore teardown-time errors from racing disconnect/reconnect states.
      }
    },
    onEvent: (handler: (event: DiscussionRealtimeEvent) => void) => {
      handlers.add(handler);
      return () => {
        handlers.delete(handler);
      };
    },
  };
};
